import { homeRow, sameSquare, squareIndex, type Occupancy } from '../board';
import { PILLAR, type MoveMods, type PawnFlags } from '../movement';
import type { MatchState, Pawn, Side, Square } from '../types';
import { epitaphSlots, handLimit, hasCarving, hasRule, hasStatus, holds, maxLinks } from './state';
import type { OmenState } from './types';

/**
 * How the Long Night changes the board: Pillars, immunity (Warded, Salt Circles, Iron Ranks,
 * Exposed, Skeleton Key) and what Carvings and statuses let a pawn do (LEDGER.MD).
 */

type LedgerSrc = Pick<MatchState, 'config' | 'ledger'>;

/** Omens on a square that are in play. */
export const omensAt = (s: Pick<MatchState, 'ledger'>, sq: Square): OmenState[] =>
  (s.ledger?.omens ?? []).filter((o) => !o.spent && sameSquare(o.square, sq));

export const omenAt = (
  s: Pick<MatchState, 'ledger'>,
  sq: Square,
  kind: OmenState['kind'],
): OmenState | undefined => omensAt(s, sq).find((o) => o.kind === kind);

/**
 * The Brazier (LEDGER.MD §6): the side's chain pawn stands on one it has just landed on. Its next
 * link may be made with any card, whatever the top, and that card burns.
 */
export function litBrazier(s: Pick<MatchState, 'ledger' | 'chain' | 'pawns'>, side: Side): boolean {
  const chain = s.chain;
  if (!s.ledger || !chain || chain.side !== side) return false;
  const sq = s.pawns[chain.pawnId]?.square;
  const last = chain.landings[chain.landings.length - 1];
  return !!sq && !!last && sameSquare(sq, last) && !!omenAt(s, sq, 'brazier');
}

/** The Looking Glass opposite one on `sq` (point-symmetric), if there is one. */
export function glassTwin(s: Pick<MatchState, 'ledger'>, sq: Square): OmenState | undefined {
  if (!omenAt(s, sq, 'looking-glass')) return undefined;
  return omenAt(s, { x: 5 - sq.x, y: 5 - sq.y }, 'looking-glass');
}

/** Rows on which a side's pawns are immune: the home row, and the next one under Iron Ranks. */
export function immuneRows(s: LedgerSrc, side: Side): number[] {
  if (!s.config.homeRowImmunity) return [];
  const home = homeRow(side);
  if (!hasRule(s, 'iron-ranks')) return [home];
  return [home, side === 'S' ? home + 1 : home - 1];
}

/** Whether `target` cannot be captured by a pawn of `by`, where it stands. */
export function immuneIn(s: LedgerSrc, target: Pawn, by: Side): boolean {
  const sq = target.square;
  if (!sq) return false;
  if (hasStatus(s, target.id, 'warded')) return true;
  if (omenAt(s, sq, 'salt-circle')) return true;
  if (!immuneRows(s, target.side).includes(sq.y)) return false;
  if (hasStatus(s, target.id, 'exposed')) return false;
  return !holds(s, by, 'skeleton-key');
}

export function pawnFlags(s: Pick<MatchState, 'ledger'>, pawn: Pawn): PawnFlags {
  return {
    passFriends: hasCarving(pawn, 'hollow-base'),
    phased: hasStatus(s, pawn.id, 'phased'),
    comet: hasCarving(pawn, 'comet'),
    hasted: hasStatus(s, pawn.id, 'hasted'),
    heavy: hasStatus(s, pawn.id, 'heavy'),
    kite: hasCarving(pawn, 'kite'),
    knightGait: hasCarving(pawn, 'knights-gait'),
    reversed: s.ledger?.sideStatuses[pawn.side].some((x) => x.kind === 'reversed') ?? false,
  };
}

export function moveMods(s: LedgerSrc): MoveMods | undefined {
  if (!s.ledger) return undefined;
  return {
    immune: (target, mover) => immuneIn(s, target, mover.side),
    jumpable: (target) => !hasCarving(target, 'iron-shell'),
    flags: (pawn) => pawnFlags(s, pawn),
  };
}

/** Puts the Pillars on an occupancy grid (in place). */
export function placePillars(s: Pick<MatchState, 'ledger'>, occ: Occupancy): Occupancy {
  for (const o of s.ledger?.omens ?? [])
    if (o.kind === 'pillar' && !o.spent) {
      const i = squareIndex(o.square);
      if (!occ[i]) occ[i] = PILLAR;
    }
  return occ;
}

/**
 * For the UI: whether a pawn is immune where it stands, for a state or a side's view. A view
 * carries every field these read (statuses, Omens, House Rules, the Mantel).
 */
export function pawnImmuneNow(
  src: { readonly config: MatchState['config']; readonly ledger?: unknown },
  pawn: Pawn,
): boolean {
  if (!src.ledger) {
    const sq = pawn.square;
    return !!sq && src.config.homeRowImmunity && sq.y === homeRow(pawn.side);
  }
  return immuneIn(src as MatchState, pawn, pawn.side === 'S' ? 'N' : 'S');
}

/** For the UI: a side's limits (hand, links, Epitaph slots) in a state or a view. */
export function limitsOf(
  src: { readonly config: MatchState['config']; readonly ledger?: unknown },
  side: Side,
): { readonly hand: number; readonly links: number; readonly epitaphSlots: number } {
  const s = src as MatchState;
  return {
    hand: handLimit(s, side),
    links: maxLinks(s, side),
    epitaphSlots: epitaphSlots(s, side),
  };
}
